import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { BookOpen, CreditCard } from 'lucide-react'
import type { ClassRosterItemView, TodayClassRosterSearchItemView } from '@/types/coaching'
import { AttendanceStatusChip } from '@/components/learner/profile/AttendanceStatusChip'
import { contentPaymentBadge } from '@/utils/contentFeeGate'
import { learnerHandle } from '@/utils/learnerEmail'

interface Props {
  items: ClassRosterItemView[]
  selectedLearnerId: number | null
  onSelect: (userId: number, timeslotId?: number) => void
  onContentPayment?: (userId: number) => void
  isLoading?: boolean
}

function hasClassContext(item: ClassRosterItemView): item is TodayClassRosterSearchItemView {
  return 'timeslotId' in item && 'className' in item
}

/**
 * 원생 목록 카드 — 이름·아이디 + 출결/교재/콘텐츠 결제 필요 상태를 노출한다.
 * 원생 선택과 콘텐츠 결제는 중첩 버튼을 피하기 위해 형제 버튼으로 분리한다.
 * 상세 정보는 하단 원생 정보 패널이 소유한다.
 */
export function ClassRoster({
  items,
  selectedLearnerId,
  onSelect,
  onContentPayment,
  isLoading,
}: Props) {
  const { t } = useTranslation('coaching')

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 p-6 text-sm text-gray-500" role="status" aria-live="polite">
        {t('dashboard.loading')}
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 p-6 text-sm text-gray-500" role="status">
        {t('roster.empty')}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('roster.groupLabel')}>
      {items.map((m) => {
        const selected = m.userId === selectedLearnerId
        const classContext = hasClassContext(m) ? m : null
        const paymentBadge = m.attendanceStatus !== 'LEAVE'
          ? contentPaymentBadge(m.contentFeeGate, m.contentFeeNotice)
          : null
        // 학습이 막힌 REQUIRED만 교재 알림을 대체한다. 유예 중·추가 결제 가능은 교재 알림과 나란히 둔다.
        const paymentRequired = paymentBadge === 'REQUIRED'
        return (
          <div
            key={m.userId}
            className={clsx(
              'flex min-w-[208px] items-stretch rounded-lg border transition-colors',
              selected
                ? 'border-teal-400 bg-teal-100/60 ring-2 ring-teal-200'
                : 'border-gray-200 bg-white hover:bg-teal-50/60',
            )}
          >
            <button
              type="button"
              aria-pressed={selected}
              onClick={() => onSelect(m.userId, classContext?.timeslotId)}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 py-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
            >
              <span className="flex min-w-0 flex-col items-start gap-1">
                <span className="flex min-w-0 max-w-full items-baseline gap-1.5">
                  <span className="shrink-0 font-semibold text-gray-900">{m.fullName}</span>
                  <span className="truncate text-xs text-gray-500">{learnerHandle(m.email)}</span>
                </span>
                {classContext ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {classContext.className} · {classContext.startTime}–{classContext.endTime}
                  </span>
                ) : null}
                {!paymentRequired && (m.textbookAlertCount ?? 0) > 0 ? (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-amber-800">
                    <BookOpen className="size-3" aria-hidden="true" />
                    {t('roster.textbookAlert')}
                  </span>
                ) : null}
              </span>
              <span className="ml-auto">
                <AttendanceStatusChip status={m.attendanceStatus} />
              </span>
            </button>
            {paymentBadge && onContentPayment ? (
              <button
                type="button"
                onClick={() => onContentPayment(m.userId)}
                className="m-1 ml-0 inline-flex shrink-0 items-center gap-1 self-center whitespace-nowrap rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
              >
                <CreditCard className="size-3" aria-hidden="true" />
                {t(paymentBadge === 'AVAILABLE' ? 'roster.contentPaymentAvailable' : 'roster.contentPaymentRequired')}
              </button>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
